import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Alert } from 'react-bootstrap';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUserMd, 
  FaCheckCircle, 
  FaUser, 
  FaPhone,
  FaMapMarkerAlt,
  FaStethoscope,
  FaArrowLeft,
  FaInfoCircle
} from 'react-icons/fa';
import CalendarView from '../components/appointment/CalendarView';
import TimeSlotPicker from '../components/appointment/TimeSlotPicker';
import '../assets/css/appointment-system.css';
import './AppointmentSystem.scss';

const AppointmentSystem = () => {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: Doctor, 2: Date, 3: Time, 4: Confirm
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [patientInfo, setPatientInfo] = useState({ 
    firstName: '', 
    lastName: '', 
    mobile: '',
    notes: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reservationCode, setReservationCode] = useState('');

  // Mock data for doctors
  const doctors = [
    { 
      id: 1, 
      name: 'دکتر سارا احمدی', 
      specialty: 'متخصص قلب و عروق',
      experience: '۱۵ سال تجربه',
      rating: 4.9,
      image: '',
      availableToday: true
    },
    { 
      id: 2, 
      name: 'دکتر مهدی رضایی', 
      specialty: 'متخصص اطفال',
      experience: '۱۲ سال تجربه',
      rating: 4.8,
      image: '',
      availableToday: true
    },
    { 
      id: 3, 
      name: 'دکتر فاطمه کریمی', 
      specialty: 'متخصص پوست و مو',
      experience: '۱۰ سال تجربه',
      rating: 4.7,
      image: '',
      availableToday: false
    },
    { 
      id: 4, 
      name: 'دکتر علی محمدی', 
      specialty: 'جراح ارتوپدی',
      experience: '۲۰ سال تجربه',
      rating: 5.0,
      image: '',
      availableToday: true
    },
  ];

  // Mock available time slots
  const getTimeSlots = (date) => {
    // Generate time slots based on date
    return [
      { id: 1, time: '۰۹:۰۰', available: true },
      { id: 2, time: '۰۹:۳۰', available: true },
      { id: 3, time: '۱۰:۰۰', available: false },
      { id: 4, time: '۱۰:۳۰', available: true },
      { id: 5, time: '۱۱:۰۰', available: true },
      { id: 6, time: '۱۴:۰۰', available: true },
      { id: 7, time: '۱۴:۳۰', available: true },
      { id: 8, time: '۱۵:۰۰', available: false },
      { id: 9, time: '۱۵:۳۰', available: true },
      { id: 10, time: '۱۶:۰۰', available: true },
      { id: 11, time: '۱۶:۳۰', available: true },
      { id: 12, time: '۱۷:۰۰', available: true },
    ];
  };

  const [timeSlots, setTimeSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    // Load blocked dates
    const today = new Date();
    const mockBlocked = [
      new Date(today.getFullYear(), today.getMonth(), 15).toISOString().split('T')[0],
      new Date(today.getFullYear(), today.getMonth(), 20).toISOString().split('T')[0]
    ];
    setBlockedDates(mockBlocked);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setTimeSlots(getTimeSlots(selectedDate));
    } else {
      setTimeSlots([]);
    }
  }, [selectedDate]);

  const handleDoctorSelect = (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedDate('');
    setSelectedTime(null);
    setCurrentStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setCurrentStep(3);
  };

  const handleTimeSelect = (timeSlot) => {
    if (timeSlot.available) {
      setSelectedTime(timeSlot);
      setCurrentStep(4);
    }
  };

  const handleReservationSubmit = (e) => {
    e.preventDefault();
    // Generate reservation code
    const code = 'RES-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    setReservationCode(code);
    setShowReservationModal(false);
    setShowConfirmation(true);
    
    // Reset after showing confirmation
    setTimeout(() => {
      setShowConfirmation(false);
      resetForm();
    }, 5000);
  };

  const resetForm = () => {
    setSelectedDoctor(null);
    setSelectedDate('');
    setSelectedTime(null);
    setCurrentStep(1);
    setPatientInfo({ firstName: '', lastName: '', mobile: '', notes: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('fa-IR', options);
  };

  const goToStep = (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  return (
    <div className="appointment-system-page">
      <Container fluid className="px-0">
        {/* Header Section */}
        <div className="appointment-header">
          <Container>
            <div className="header-content">
              <div className="header-text">
                <h1 className="main-title">
                  <FaStethoscope className="title-icon" />
                  رزرو نوبت آنلاین
                </h1>
                <p className="subtitle">در ۴ مرحله ساده نوبت خود را رزرو کنید</p>
              </div>
              <div className="header-steps">
                <div className={`step-indicator ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                  <div className="step-number">۱</div>
                  <div className="step-label">انتخاب پزشک</div>
                </div>
                <div className={`step-indicator ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                  <div className="step-number">۲</div>
                  <div className="step-label">انتخاب تاریخ</div>
                </div>
                <div className={`step-indicator ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                  <div className="step-number">۳</div>
                  <div className="step-label">انتخاب زمان</div>
                </div>
                <div className={`step-indicator ${currentStep >= 4 ? 'active' : ''}`}>
                  <div className="step-number">۴</div>
                  <div className="step-label">تایید نهایی</div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Container className="main-content">
          <Row>
            {/* Main Content Area */}
            <Col lg={selectedDoctor ? 8 : 12}>
              <div className="booking-steps">
                {/* Step 1: Select Doctor */}
                {currentStep === 1 && (
                  <Card className="step-card">
                    <Card.Body>
                      <Alert variant="light" className="step-info-alert">
                        <FaInfoCircle />
                        <span>
                          <strong>مرحله ۱:</strong> از میان پزشکان زیر، متخصص مناسب برای حیوان خود را انتخاب کنید.
                        </span>
                      </Alert>
                      <div className="step-header">
                        <h2 className="step-title">
                          <FaUserMd className="step-icon" />
                          انتخاب پزشک
                        </h2>
                        <p className="step-description">پزشک مورد نظر خود را انتخاب کنید</p>
                      </div>
                      
                      <div className="doctors-grid">
                        {doctors.map((doctor) => (
                          <Card 
                            key={doctor.id}
                            className={`doctor-card ${selectedDoctor?.id === doctor.id ? 'selected' : ''}`}
                            onClick={() => handleDoctorSelect(doctor)}
                          >
                            <Card.Body>
                              <div className="doctor-avatar">
                                <FaUserMd />
                              </div>
                              <div className="doctor-info">
                                <h4 className="doctor-name">{doctor.name}</h4>
                                <p className="doctor-specialty">{doctor.specialty}</p>
                                <div className="doctor-meta">
                                  <span className="experience">{doctor.experience}</span>
                                  <div className="rating">
                                    <span className="rating-value">{doctor.rating}</span>
                                    <span className="rating-star">⭐</span>
                                  </div>
                                </div>
                                {doctor.availableToday && (
                                  <Badge bg="success" className="availability-badge">
                                    امروز در دسترس
                                  </Badge>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Step 2: Select Date */}
                {currentStep === 2 && selectedDoctor && (
                  <Card className="step-card">
                    <Card.Body>
                      <div className="step-header">
                        <Button
                          variant="link"
                          className="back-button"
                          onClick={() => goToStep(1)}
                        >
                          <FaArrowLeft /> بازگشت
                        </Button>
                        <div>
                          <h2 className="step-title">
                            <FaCalendarAlt className="step-icon" />
                            انتخاب تاریخ
                          </h2>
                          <p className="step-description">
                            تاریخ مناسب را برای نوبت با <strong>{selectedDoctor.name}</strong> انتخاب کنید
                          </p>
                        </div>
                      </div>

                      <Alert variant="light" className="step-info-alert">
                        <FaInfoCircle />
                        <span>
                          <strong>نکته:</strong> روزهای سبز قابل رزرو و روزهای قرمز مسدود هستند.
                        </span>
                      </Alert>
                      
                      <div className="calendar-wrapper">
                        <CalendarView
                          selectedDate={selectedDate}
                          onDateSelect={handleDateSelect}
                          blockedDates={blockedDates}
                          reservations={[]}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Step 3: Select Time */}
                {currentStep === 3 && selectedDoctor && selectedDate && (
                  <Card className="step-card">
                    <Card.Body>
                      <div className="step-header">
                        <Button
                          variant="link"
                          className="back-button"
                          onClick={() => goToStep(2)}
                        >
                          <FaArrowLeft /> بازگشت
                        </Button>
                        <div>
                          <h2 className="step-title">
                            <FaClock className="step-icon" />
                            انتخاب زمان
                          </h2>
                          <p className="step-description">
                            زمان مناسب را برای تاریخ <strong>{formatDate(selectedDate)}</strong> انتخاب کنید
                          </p>
                        </div>

                        <Alert variant="light" className="step-info-alert">
                          <FaInfoCircle />
                          <span>
                            <strong>راهنما:</strong> زمان‌های صبح، بعدازظهر و عصر بر اساس ظرفیت خالی نمایش داده می‌شوند.
                          </span>
                        </Alert>
                      </div>
                      
                      <div className="time-picker-wrapper">
                        <TimeSlotPicker
                          timeSlots={timeSlots}
                          selectedTime={selectedTime}
                          onTimeSelect={handleTimeSelect}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Step 4: Confirm */}
                {currentStep === 4 && selectedDoctor && selectedDate && selectedTime && (
                  <Card className="step-card">
                    <Card.Body>
                      <div className="step-header">
                        <Button
                          variant="link"
                          className="back-button"
                          onClick={() => goToStep(3)}
                        >
                          <FaArrowLeft /> بازگشت
                        </Button>
                        <div>
                          <h2 className="step-title">
                            <FaCheckCircle className="step-icon" />
                            تایید نهایی
                          </h2>
                          <p className="step-description">اطلاعات رزرو خود را بررسی و تایید کنید</p>
                        </div>

                        <Alert variant="light" className="step-info-alert">
                          <FaInfoCircle />
                          <span>
                            <strong>لطفا بررسی کنید:</strong> پس از تایید، تغییر نوبت فقط با تماس با کلینیک امکان‌پذیر است.
                          </span>
                        </Alert>
                      </div>
                      
                      <div className="confirmation-summary">
                        <Card className="summary-card">
                          <Card.Body>
                            <div className="summary-item">
                              <div className="summary-icon">
                                <FaUserMd />
                              </div>
                              <div className="summary-content">
                                <span className="summary-label">پزشک</span>
                                <span className="summary-value">{selectedDoctor.name}</span>
                                <span className="summary-sub">{selectedDoctor.specialty}</span>
                              </div>
                            </div>
                            
                            <div className="summary-item">
                              <div className="summary-icon">
                                <FaCalendarAlt />
                              </div>
                              <div className="summary-content">
                                <span className="summary-label">تاریخ</span>
                                <span className="summary-value">{formatDate(selectedDate)}</span>
                              </div>
                            </div>
                            
                            <div className="summary-item">
                              <div className="summary-icon">
                                <FaClock />
                              </div>
                              <div className="summary-content">
                                <span className="summary-label">زمان</span>
                                <span className="summary-value">{selectedTime.time}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>

                        <Button
                          className="confirm-button"
                          size="lg"
                          onClick={() => setShowReservationModal(true)}
                        >
                          ادامه و تکمیل اطلاعات
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Col>

            {/* Sidebar Summary */}
            {selectedDoctor && (
              <Col lg={4}>
                <div className="booking-sidebar">
                  <Card className="sidebar-card sticky-sidebar">
                    <Card.Header className="sidebar-header">
                      <h3>خلاصه رزرو</h3>
                    </Card.Header>
                    <Card.Body>
                      {selectedDoctor && (
                        <div className="sidebar-item">
                          <div className="sidebar-icon">
                            <FaUserMd />
                          </div>
                          <div className="sidebar-content">
                            <span className="sidebar-label">پزشک</span>
                            <span className="sidebar-value">{selectedDoctor.name}</span>
                          </div>
                        </div>
                      )}

                      {selectedDate && (
                        <div className="sidebar-item">
                          <div className="sidebar-icon">
                            <FaCalendarAlt />
                          </div>
                          <div className="sidebar-content">
                            <span className="sidebar-label">تاریخ</span>
                            <span className="sidebar-value">{formatDate(selectedDate)}</span>
                          </div>
                        </div>
                      )}

                      {selectedTime && (
                        <div className="sidebar-item">
                          <div className="sidebar-icon">
                            <FaClock />
                          </div>
                          <div className="sidebar-content">
                            <span className="sidebar-label">زمان</span>
                            <span className="sidebar-value">{selectedTime.time}</span>
                          </div>
                        </div>
                      )}

                      {!selectedDate && (
                        <Alert variant="info" className="sidebar-alert">
                          <FaInfoCircle /> لطفا تاریخ و زمان را انتخاب کنید
                        </Alert>
                      )}

                      {selectedDoctor && selectedDate && selectedTime && (
                        <Button
                          className="sidebar-button"
                          size="lg"
                          onClick={() => setShowReservationModal(true)}
                        >
                          تکمیل اطلاعات و رزرو
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </Container>

      {/* Reservation Modal */}
      <Modal 
        show={showReservationModal} 
        onHide={() => setShowReservationModal(false)}
        centered
        size="lg"
        className="reservation-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>تکمیل اطلاعات رزرو</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleReservationSubmit}>
          <Modal.Body>
            <div className="modal-summary">
              <div className="modal-summary-item">
                <strong>پزشک:</strong> {selectedDoctor?.name}
              </div>
              <div className="modal-summary-item">
                <strong>تاریخ:</strong> {formatDate(selectedDate)}
              </div>
              <div className="modal-summary-item">
                <strong>زمان:</strong> {selectedTime?.time}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaUser className="me-2" />
                نام
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="نام خود را وارد کنید"
                value={patientInfo.firstName}
                onChange={(e) => setPatientInfo({...patientInfo, firstName: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaUser className="me-2" />
                نام خانوادگی
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="نام خانوادگی خود را وارد کنید"
                value={patientInfo.lastName}
                onChange={(e) => setPatientInfo({...patientInfo, lastName: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaPhone className="me-2" />
                شماره تماس
              </Form.Label>
              <Form.Control
                type="tel"
                placeholder="09123456789"
                value={patientInfo.mobile}
                onChange={(e) => setPatientInfo({...patientInfo, mobile: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>توضیحات (اختیاری)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="توضیحات اضافی را اینجا وارد کنید..."
                value={patientInfo.notes}
                onChange={(e) => setPatientInfo({...patientInfo, notes: e.target.value})}
              />
            </Form.Group>

            <Alert variant="info" className="mt-3">
              <FaInfoCircle className="me-2" />
              با رزرو این نوبت، شما با شرایط و قوانین ما موافقت می‌کنید.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReservationModal(false)}>
              لغو
            </Button>
            <Button variant="primary" type="submit" className="btn-primary-gradient">
              تایید و ثبت نوبت
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal 
        show={showConfirmation} 
        onHide={() => setShowConfirmation(false)} 
        centered
        className="confirmation-modal"
      >
        <Modal.Body className="text-center py-5">
          <div className="confirmation-icon-wrapper">
            <FaCheckCircle className="confirmation-icon" />
          </div>
          <h3 className="confirmation-title">نوبت شما با موفقیت ثبت شد!</h3>
          <div className="reservation-code-display">
            <span className="code-label">کد رزرو:</span>
            <span className="code-value">{reservationCode}</span>
          </div>
          <div className="confirmation-details">
            <p><strong>پزشک:</strong> {selectedDoctor?.name}</p>
            <p><strong>تاریخ و زمان:</strong> {formatDate(selectedDate)} در {selectedTime?.time}</p>
            <p><strong>بیمار:</strong> {patientInfo.firstName} {patientInfo.lastName}</p>
          </div>
          <p className="confirmation-message">
            اطلاعات رزرو به شماره تماس شما ارسال خواهد شد.
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AppointmentSystem;
